import React, { useState } from "react";
import { Layout, Menu, Breadcrumb } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const { Content, Sider } = Layout;

const graphs = [
  {
    id: "graph1",
    title: "Indicadores",
    menuTitle: "Indicadores",
    text: `En el siguiente gráfico se presenta la proporción de niñas y niños carentes en cada uno de los indicadores que componen la pobreza multidimensional 
          de acuerdo a la metodología 2015 (15 indicadores) y a la metodología 2024 (20 indicadores). 

  Según la metodología 2024, en el último año de la estimación, los indicadores que presentan una mayor proporción de niñas y niños carentes son informalidad 
          (33%), seguridad (31%), ocupación y subempleo (26%) y control preventivo de salud (26%). Este último es el que presenta una mayor disminución desde el 
          2022, donde la proporción correspondía a un 34% de las niñas y niños carentes en el control preventivo de salud.`,
    link: "https://public.flourish.studio/visualisation/27483359/embed",
  },
  {
    id: "graph2",
    title: "Evolución en el tiempo",
    menuTitle: "Evolución en el tiempo",
    text: `De acuerdo a la metodología 2024, es decir, la metodología vigente, la incidencia de la pobreza multidimensional en niñas y niños se ubica 
          en un 23% en la última medición. Esta proporción disminuyó 3 puntos desde el 2022. 

  La metodología 2015 nos permite ver una serie de tiempo más larga, para conocer la evolución de la pobreza multidimensional en Chile. Se observa que 
          la proporción de niñas y niños en hogares con pobreza multidimensional ha disminuido desde que está disponible esta estimación.`,
    link: "https://public.flourish.studio/visualisation/27214984/embed",
  },
  {
    id: "graph3",
    title: "Análisis por sexo",
    menuTitle: "\u2022 Sexo",
    text: `No hay diferencias en la proporción de niñas y niños en hogares con pobreza multidimensional según el sexo, tanto en la metodología del 2015 como la 
          del 2024. Es importante señalar, sin embargo, que la medición de pobreza en Chile se realiza a nivel de hogar, por lo que no es posible conocer las 
          experiencias individuales y, con ello, las diferencias de género que podrían haber.`,
    link: "https://public.flourish.studio/visualisation/27214987/embed",
  },
  {
    id: "graph4",
    title: "Análisis por edad",
    menuTitle: "\u2022 Edad",
    text: `Con la nueva metodología de 2024, no se observan diferencias estadísticamente significativas entre tramos etarios, ni en la medición de 2022 ni en 
          la de 2024. Las diferencias que se observaban con la metodología 2015, donde la primera infancia mostraba una mayor incidencia de pobreza multidimensional, 
          desaparecen con la nueva medición.`,
    link: "https://public.flourish.studio/visualisation/27215060/embed",
  },
  {
    id: "graph5",
    title: "Análisis por país de nacimiento",
    menuTitle: "\u2022 País de nacimiento",
    text: `Con la metodología 2024, la incidencia de la pobreza multidimensional es casi el doble entre las niñas y niños que nacieron en otro país, con un 40% 
          y un 21%, respectivamente. En ambos grupos disminuyó la pobreza multidimensional desde 2022 en 4 puntos porcentuales.  

  Esta tendencia se replica con la metodología 2015, en tanto que las niñas y niños nacidos en otro país tienden a vivir en hogares con pobreza multidimensional 
          en mayor proporción.`,
    link: "https://public.flourish.studio/visualisation/27215089/embed",
  },
  {
    id: "graph6",
    title: "Análisis por pertenencia a pueblo originario",
    menuTitle: "\u2022 Pueblo originario",
    text: `Con ambas metodologías, las niñas y niños en hogares que se identifican con pueblos originarios presentan una mayor proporción que viven en hogares 
          con pobreza multidimensional que aquellos que no se identifican. En 2024, con la nueva metodología, la brecha entre ambos grupos es de 6 puntos porcentuales. 
          Siguiendo la tendencia general, la incidencia de pobreza multidimensional disminuye respecto al 2022.`,
    link: "https://public.flourish.studio/visualisation/27215172/embed",
  },
  {
    id: "graph7",
    title: "Análisis por quintil de ingresos",
    menuTitle: "\u2022 Quintil de ingresos",
    text: `Ambas metodologías comparten la misma tendencia: a menores ingresos, mayor es la incidencia de pobreza multidimensional en la niñez. Según la nueva 
          medición (2024), esta situación afecta al 42% de las niñas y niños del primer quintil y al 26% del segundo. Es recién en el tercer quintil de ingreso 
          en que la incidencia de pobreza multidimensional es menor que el promedio nacional (23%).`,
    link: "https://public.flourish.studio/visualisation/27216131/embed",
  },
  {
    id: "graph8",
    title: "Análisis por estructura familiar",
    menuTitle: "\u2022 Estructura familiar",
    text: `Independientemente de la metodología, la tendencia es constante: las niñas y niños en hogares extensos (monoparentales o biparentales) presentan una 
          mayor proporción de pobreza multidimensional.  Estas diferencias se explican principalmente por el tamaño del hogar, ya que a mayor cantidad personas 
          hay más probabilidades de acumular carencias al interior del hogar. Mientras los hogares nucleares promedian entre 2,9 y 3,9 personas, los extensos 
          suben a 4,7 (monoparentales) y 5,6 (biparentales).

  No obstante, el indicador de pobreza severa revela con mayor detalle las dinámicas de la pobreza: las niñas y niños en hogares nucleares monoparentales son 
          los que muestran mayor incidencia de pobreza por ingreso. 

  En 2024, de acuerdo a la nueva metodología, un 39% de las niñas y niños en hogares extensos biparentales viven con pobreza multidimensional, cifra que desciende 
          a la mitad (17%) en el caso de los nucleares biparentales`,
    link: "https://public.flourish.studio/visualisation/27215831/embed",
  },
  {
    id: "graph9",
    title: "Análisis por zona de residencia",
    menuTitle: "\u2022 Zona",
    text: `Con la nueva metodología 2024, no se observan diferencias estadísticamente significativas según zona de residencia, a diferencia de la metodología anterior 
          que arrojaba una brecha de 10 puntos. Lo interesante es que la proporción de niñas y niños de zonas rurales en hogares con pobreza multidimensional no varía; 
          el incremento se explica casi totalmente por lo que ocurre en las zonas urbanas. Esto indicaría que la actualización metodológica logró visibilizar carencias 
          de la ciudad que antes pasaban desapercibidas.`,
    link: "https://public.flourish.studio/visualisation/27215130/embed",
  },
  {
    id: "graph10",
    title: "Región",
    menuTitle: "\u2022 Región",
    text: `En 2024, de acuerdo a la nueva metodología, la región que presenta una mayor proporción de niñas y niños en hogares con pobreza multidimensional es la de 
          Tarapacá, con un 29%, seguido por la de Atacama (26%) y la Metropolitana (25%). Por otro lado, la región de Magallanes es la que presenta una menor proporción, 
          con un 9%.`,
    link: "https://public.flourish.studio/visualisation/27215856/embed",
  },
];


const App = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [selectedMenuItem, setSelectedMenuItem] = useState("graph1");

  const handleMenuItemClick = (graphId, graphTitle) => {
    setSelectedMenuItem(graphId);
  };

  const toggleCollapsed = () => {
    setCollapsed(!collapsed);
  };

  const selectedGraph = graphs.find((graph) => graph.id === selectedMenuItem);

  return (
    <Layout style={{ minHeight: "100vh" }}>
      <Sider
        width={250}
        collapsible
        collapsed={collapsed}
        onCollapse={toggleCollapsed}
        style={{ background: "#fff" }}
        breakpoint="lg"
        collapsedWidth={0}
      >
        <Menu theme="dark" mode="vertical" selectedKeys={[selectedMenuItem]} style={{ background: "#fff" }}>
          {graphs.map((graph) => (
            <Menu.Item
              key={graph.id}
              onClick={() => handleMenuItemClick(graph.id)}
              style={{ color: 'black', paddingLeft: (graph.id === "graph1" || graph.id === "graph2") ? "20px" : "30px" }}
            >
              {graph.menuTitle}
            </Menu.Item>
          ))}
        </Menu>
      </Sider>
      <Layout style={{ padding: "24px 24px 24px" }}>
        <Breadcrumb style={{ margin: "16px 0" }}>
          <Breadcrumb.Item></Breadcrumb.Item>
        </Breadcrumb>
        <Content
          id="content"
          style={{
            background: "#ffff",
            padding: 24,
            margin: 0,
            minHeight: 280,
          }}
        >
          {selectedGraph && (
            <div>
              <div style={{ marginBottom: 16 }}>
                <h1>{selectedGraph.title}</h1>
              </div>
              <div style={{ textAlign: 'justify' }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {selectedGraph.text}
                </ReactMarkdown>
              </div>
              <iframe
                src={selectedGraph.link}
                frameBorder="0"
                scrolling="no"
                style={{ minWidth: "100%", height: "650px" }}
              ></iframe>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default App;
